import ENEMYS from "../../consts/ENEMYS";
import EnemyType from "../../enums/EnemyType";
import Game from "../Game";
import EnemyGroup from "./EnemyGroup";
import Player from "../Player";
import GameCore from "../GameCore";
import HitVFXType from "../../enums/HitVFXType";
import BOSS_ENEMYS from "../../consts/enemys/BOSS_ENEMYS";
import GREEN_STAGES_BOSSES from "../../consts/enemys/GREEN_STAGES_BOSSES";
import PickupType from "../../enums/PickupType";
import hapticFeedback from "../../../utils/haptics";
import { CaravanElement, RewardReference } from "../../../handlers/types";
import CoinPickup from "../pickup/CoinPickup";
import {
  SPRITESHEET_KEYS,
  SPRITESHEET_FRAMES,
} from "../../consts/SPRITESHEETS";

export default class Enemy extends Phaser.Physics.Arcade.Sprite {
  hp: number;
  maxHp: number;
  dataMaxHp: number;
  power: number;
  knockback: number;
  deathKB: number;
  damageKB: number;
  xp: number;
  speed: number;
  scaleMul: number;
  enemyType: EnemyType;
  HPxLevel: boolean;
  FixedDirection: boolean;
  owner: null;
  isDead: boolean;
  receivingDamage: boolean;
  _currentDirection: Phaser.Math.Vector2;
  defaultName: string;
  isCullable: boolean;
  IsTimeStopped: boolean;
  IsFrozen: boolean;
  defaultSpeed: number;
  res_Freeze: number;
  res_Rosary: number;
  moveTweenIndex: number;
  isTeleportOnCull: boolean;
  pool: EnemyGroup;
  colliderOverride: any;
  target: Player;
  private _blinkTimeout: Phaser.Time.TimerEvent;
  treasure: any;
  FreezeTimer: Phaser.Time.TimerEvent;
  timeOutsideScreen: number;
  inFormation: boolean = false;
  isOutsideView: boolean = true;
  enemyData: any = null; // Store enemy data for objective checks
  private bossHealthBar: Phaser.GameObjects.Graphics | null = null;
  private bossHealthBarWidth: number = 50;
  private bossHealthBarHeight: number = 4;
  private bossHealthBarOffset: Phaser.Math.Vector2 = new Phaser.Math.Vector2(
    0,
    5
  );
  bossIdAndValue?: RewardReference; // Store boss ID and value for treasure claiming

  static PHYSICS_BODY_RADIUS = 16; // Default radius for physics body
  static readonly DEPTH_SORT_INTERVAL = 10; // Update depth every N frames
  static readonly VIEW_CHECK_INTERVAL = 5; // Check view every N frames
  static readonly TIME_BEFORE_PROCESS_OUTSIDE_VIEW = 2000; // Time (ms) before processing enemy outside view
  private frameCounter = 0;
  private viewCheckCounter = 0;

  constructor(group: EnemyGroup, x: number, y: number, enemyType: EnemyType) {
    super(group.scene, x, y, "");

    this.hp = 3;
    this.maxHp = 3;
    this.dataMaxHp = 3;
    this.power = 10;
    this.knockback = 10;
    this.deathKB = 10;
    this.damageKB = 1;
    this.xp = 1;
    this.speed = 100;
    this.scaleMul = 1;
    this.enemyType = enemyType;
    this.HPxLevel = false;
    this.FixedDirection = false;
    this.owner = null;
    this.isDead = false;
    this.receivingDamage = false;
    this._currentDirection = new Phaser.Math.Vector2(0, 0);
    this.defaultName = "";
    this.isCullable = true;
    this.IsTimeStopped = false;
    this.IsFrozen = false;
    this.defaultSpeed = 100;
    this.res_Freeze = 0;
    this.res_Rosary = 0;
    this.moveTweenIndex = 0;
    this.isTeleportOnCull = false;
    this.pool = group;
    this.timeOutsideScreen = 0;
  }

  get isBoss(): boolean {
    return !!(
      BOSS_ENEMYS[this.enemyType] || GREEN_STAGES_BOSSES[this.enemyType]
    );
  }

  // Helper function to determine if enemy should use spritesheet
  private getSpritesheetInfo(spriteName: string): {
    usesSpritesheet: boolean;
    spritesheetKey?: string;
    frameName?: string;
  } {
    // Map sprite names to their appropriate difficulty-based spritesheet and first frame
    const easyEnemies: { [key: string]: string } = {
      small_hamster1: SPRITESHEET_FRAMES.EASY_ENEMIES.SMALL_HAMSTER1_0,
      small_hamster2: SPRITESHEET_FRAMES.EASY_ENEMIES.SMALL_HAMSTER2_0,
      fat_hamster: SPRITESHEET_FRAMES.EASY_ENEMIES.FAT_HAMSTER_0,
      scorpion: SPRITESHEET_FRAMES.EASY_ENEMIES.SCORPION_0,
      snake1: SPRITESHEET_FRAMES.EASY_ENEMIES.SNAKE1_0,
      snake2: SPRITESHEET_FRAMES.EASY_ENEMIES.SNAKE2_0,
      cactus: SPRITESHEET_FRAMES.EASY_ENEMIES.CACTUS_0,
      warrior_hamster: SPRITESHEET_FRAMES.EASY_ENEMIES.WARRIOR_HAMSTER_0,
    };

    const mediumEnemies: { [key: string]: string } = {
      vulture: SPRITESHEET_FRAMES.MEDIUM_ENEMIES.VULTURE_0,
      golem: SPRITESHEET_FRAMES.MEDIUM_ENEMIES.GOLEM_0,
    };

    const difficultEnemies: { [key: string]: string } = {
      reptile: SPRITESHEET_FRAMES.DIFFICULT_ENEMIES.REPTILE_0,
    };

    const bossEnemies: { [key: string]: string } = {
      suit_hamster: SPRITESHEET_FRAMES.BOSS_ENEMIES.SUIT_HAMSTER_0,
      vampire_hamster: SPRITESHEET_FRAMES.BOSS_ENEMIES.VAMPIRE_HAMSTER_0,
      lizard_hamster: SPRITESHEET_FRAMES.BOSS_ENEMIES.LIZARD_HAMSTER_0,
      king_hamster: SPRITESHEET_FRAMES.BOSS_ENEMIES.KING_HAMSTER_0,
      dude: SPRITESHEET_FRAMES.BOSS_ENEMIES.DUDE_0,
    };

    if (easyEnemies[spriteName]) {
      return {
        usesSpritesheet: true,
        spritesheetKey: SPRITESHEET_KEYS.EASY_ENEMIES,
        frameName: easyEnemies[spriteName],
      };
    }

    if (mediumEnemies[spriteName]) {
      return {
        usesSpritesheet: true,
        spritesheetKey: SPRITESHEET_KEYS.MEDIUM_ENEMIES,
        frameName: mediumEnemies[spriteName],
      };
    }

    if (difficultEnemies[spriteName]) {
      return {
        usesSpritesheet: true,
        spritesheetKey: SPRITESHEET_KEYS.DIFFICULT_ENEMIES,
        frameName: difficultEnemies[spriteName],
      };
    }

    if (bossEnemies[spriteName]) {
      return {
        usesSpritesheet: true,
        spritesheetKey: SPRITESHEET_KEYS.BOSS_ENEMIES,
        frameName: bossEnemies[spriteName],
      };
    }

    return { usesSpritesheet: false };
  }

  Init(x: number = -1000, y: number = -1000) {
    const enemyData = ENEMYS[this.enemyType]?.[0];
    if (!enemyData) {
      console.error(`Enemy data not found for enemy type: ${this.enemyType}`);
      return;
    }

    // Store enemy data for objective checks
    this.enemyData = enemyData;

    this.dataMaxHp = enemyData.maxHp;
    this.maxHp = enemyData.maxHp;
    this.power = enemyData.power;
    this.knockback = enemyData.knockback;
    this.deathKB = enemyData.deathKB;
    this.xp = enemyData.xp;
    this.speed = enemyData.speed;
    this.defaultSpeed = enemyData.speed;
    this.alpha = enemyData.alpha ? enemyData.alpha : 1;
    this.scaleMul = enemyData.scale ? enemyData.scale : 1;
    this.res_Freeze = enemyData.res_Freeze ? enemyData.res_Freeze : 0;
    this.res_Rosary = enemyData.res_Rosary ? enemyData.res_Rosary : 0;
    this.colliderOverride = enemyData.colliderOverride;

    const skills = enemyData.skills;
    if (skills) {
      if (skills.includes("HPxLevel")) {
        this.HPxLevel = true;
      }
      if (skills.includes("FixedDirection")) {
        this.FixedDirection = true;
      }
    }

    // Check if enemy should use spritesheet
    const spritesheetInfo = this.getSpritesheetInfo(enemyData.spriteName);

    if (
      spritesheetInfo.usesSpritesheet &&
      spritesheetInfo.spritesheetKey &&
      spritesheetInfo.frameName
    ) {
      // Use spritesheet
      this.setTexture(
        spritesheetInfo.spritesheetKey,
        spritesheetInfo.frameName
      );
      if (enemyData.animationKey) {
        this.play(enemyData.animationKey);
      }
    } else {
      // Use individual texture/spritesheet
      if (enemyData.animationKey) {
        this.play(enemyData.animationKey);
      } else {
        this.setFrame(enemyData.spriteName);
      }
    }

    this.setPosition(x, y);
    this.scene.physics.add.existing(this);

    // Bosses should not be cullable by the ContainmentRect system
    if (this.isBoss) {
      this.isCullable = false;
    }

    this.OnRecycle();

    // Optimize physics body - use a smaller, fixed size collision circle
    const radius = this.colliderOverride?.radius
      ? Enemy.PHYSICS_BODY_RADIUS * this.colliderOverride.radius
      : Enemy.PHYSICS_BODY_RADIUS;

    this.Body.setCircle(radius, 0, 0);

    if (this.colliderOverride?.offsetY) {
      this.Body.setOffset(
        radius * 0.5,
        radius * 0.5 + this.colliderOverride.offsetY
      );
    }

    this.setOrigin(0.5, 1);
    this.setScale(GameCore.PixelScale * this.scaleMul);
  }

  Freeze(delay: number, t = 1) {
    if (!(this.res_Freeze > t || this.IsTimeStopped)) {
      if (this.FreezeTimer) {
        this.FreezeTimer.destroy();
      }
      this.FreezeTimer = this.scene.time.addEvent({
        delay,
        callback: () => this.ResumeFromFreeze(),
      });
      this.IsTimeStopped = true;
      this.speed = 0;
      this.setVelocity(0);
      this.setTint(255);
    }
  }
  ResumeFromFreeze() {
    this.IsTimeStopped = false;
    this.speed = this.defaultSpeed;
    this.restoreTint();
  }
  TimeStop() {
    if (this.FreezeTimer) {
      this.FreezeTimer.destroy();
      this.IsTimeStopped = true;
      this.speed = 0;
      this.setVelocity(0);
      this.setTint(255);
    }
  }
  ResumeFromTimeStop() {
    this.IsTimeStopped = false;
    this.speed = this.defaultSpeed;
    this.restoreTint();
  }
  setOwner(owner: any) {
    this.owner = owner;
  }

  setBossId(bossIdAndValue: RewardReference) {
    this.bossIdAndValue = bossIdAndValue;
  }
  GetExploded() {
    // Only apply explosion damage if enemy is visible on screen
    if (this.isDead || this.isOutsideView) return;

    const damage = Math.max(this.maxHp * 0.35, 100);

    this.GetDamaged(damage, HitVFXType.DEFAULT, 0);
  }
  GetDamaged(
    PPower: number = 1,
    hitVFX: HitVFXType = HitVFXType.DEFAULT,
    knockback: number
  ) {
    if (PPower > 0 && Game.Core.PlayerOptions.DamageNumbersEnabled) {
      Game.Core.ShowDamageAt(this.x, this.y, PPower);
    }
    this.hp -= PPower;
    if (this.isBoss && this.bossHealthBar) {
      this.updateBossHealthBar();
    }
    if (this.hp <= 0) {
      this.Die();
    } else {
      this.damageKB = knockback;
    }
    // TODO: Play Sound
    this.OnGetDamaged(hitVFX);
  }

  Die() {
    if (this.isDead) return;
    this.isDead = true;

    // Clear the boss health bar immediately when boss dies
    if (this.isBoss && this.bossHealthBar) {
      this.bossHealthBar.clear();
    }

    // Play coordinated death audio and VFX using pooled coordination
    // Sound settings are automatically handled by the facade
    Game.Core.poolManager?.showEnemyDeathWithAudio(
      this.x,
      this.y,
      Math.max(this.height / 64, 1) * 3 // Assuming enemy_death sprite is 64px
    );

    if (hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred("medium");
    }

    // Get caravan element if this is a camel
    const caravanElement =
      this.enemyType === EnemyType.CAMEL
        ? (this.getData("caravanElement") as CaravanElement)
        : null;

    // Check if enemy has DropCoin skill and drop a coin
    if (this.enemyData?.skills?.includes("DropCoin")) {
      this.DropCoin();
    }

    // Drop a chest ONLY if this is a special camel
    if (caravanElement && caravanElement.type === "special") {
      const pickup = Game.Core.MakePickup(
        this.x,
        this.y,
        PickupType.CAMEL_CHEST
      );

      // Pass the special value and gift to the chest
      if (caravanElement.specialValue) {
        pickup.setData("specialValue", caravanElement.specialValue);
      }
      if (caravanElement.specialGift) {
        pickup.setData("specialGift", caravanElement.specialGift);
      }
    }
    this.DeSpawn();

    Game.Core.scene.time.removeEvent(this._blinkTimeout);
    const xp = Math.floor((Math.random() + 0.5) * this.xp);
    if (xp > 0) {
      Game.Core.MakeGem(this.x, this.y, xp);
    }

    if (this.isBoss) {
      Game.Core.MakeTreasure(this.x, this.y, this.bossIdAndValue);
    }

    const enemyData = ENEMYS[this.enemyType]?.[0];
    if (enemyData) {
      enemyData.killedAmount += 1;
    }
    Game.Core.PlayerOptions.RunEnemies += 1;
    Game.Core.MainUI.UpdateKills();
  }

  OnGetDamaged(hitVFX: HitVFXType = HitVFXType.DEFAULT) {
    if (
      Game.Core.PlayerOptions.FlashingVFXEnabled &&
      hitVFX !== HitVFXType.NONE
    ) {
      this.setTintFill();
      // TODO: Special effects evaluation
    }

    if (hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred("light");
    }

    this._blinkTimeout = Game.Core.scene.time.addEvent({
      delay: this.isDead ? 60 : 120,
      loop: false,
      callback: () => {
        this.restoreTint();
      },
    });
    this.receivingDamage = true;
  }

  restoreTint() {
    this.IsTimeStopped ? this.setTint(255) : this.setTint(0xffffff);
    this.receivingDamage = false;
  }

  OnRecycle() {
    this.damageKB = 1;
    this.target = Game.Core.Player;
    if (this.HPxLevel) {
      this.maxHp = Game.Core.Player.level * this.dataMaxHp;
    }
    if (this.FixedDirection) {
      this._currentDirection.x = 0;
      this._currentDirection.y = 0;
    }
    this.hp = this.maxHp;
    this.isDead = false;
    this.visible = true;
    this.Body.enable = true;
    this.Body.checkCollision.none = false; // Reset collision checking on recycle
    this.IsTimeStopped = false;
    this.speed = this.defaultSpeed;
    this.restoreTint();
    this.setOrigin(0.5);
    this.setScale(1);

    // Reset texture if enemy uses spritesheet
    if (this.enemyData && this.enemyData.spriteName) {
      const spritesheetInfo = this.getSpritesheetInfo(
        this.enemyData.spriteName
      );
      if (
        spritesheetInfo.usesSpritesheet &&
        spritesheetInfo.spritesheetKey &&
        spritesheetInfo.frameName
      ) {
        this.setTexture(
          spritesheetInfo.spritesheetKey,
          spritesheetInfo.frameName
        );
      }
    }

    const minSide = Math.min(this.width, this.height);
    let radius = minSide * 0.4;

    if (this.colliderOverride) {
      radius = minSide * this.colliderOverride.radius;
      const offset = radius * 0.5;
      this.Body.setOffset(offset, offset + this.colliderOverride.offsetY);
    }

    // Adjust health bar size based on enemy scale
    if (this.isBoss) {
      this.bossHealthBarWidth = 50 * this.scaleMul;
      this.bossHealthBarOffset.y = 5 * this.scaleMul;
    }

    this.Body.setCircle(radius, 0, 0);
    this.setOrigin(0.5, 1);
    this.setScale(GameCore.PixelScale * this.scaleMul);
    this.timeOutsideScreen = 0;

    if (this.isBoss) {
      // Create health bar if it doesn't exist
      if (!this.bossHealthBar) {
        this.bossHealthBar = this.scene.add.graphics();
      }

      // Update the health bar
      this.updateBossHealthBar();
    } else if (this.bossHealthBar) {
      // Hide health bar for non-boss enemies
      this.bossHealthBar.clear();
    }
  }

  get Body() {
    return this.body as Phaser.Physics.Arcade.Body;
  }
  OnTeleportOnCull() {
    var e = Game.Core.GetPositionOutOfSight(5, 48);
    this.setPosition(e.x, e.y);
  }
  Update(delta: number): void {
    if (this.isDead || this.IsTimeStopped) return;

    // Update health bar position if this is a boss
    if (this.isBoss && this.bossHealthBar) {
      this.updateBossHealthBar();
    }

    // Only update depth sorting periodically
    this.frameCounter = (this.frameCounter + 1) % Enemy.DEPTH_SORT_INTERVAL;
    if (this.frameCounter === 0) {
      this.setDepth(this.y - Game.Core.Player.y);
    }

    // Skip normal movement if in formation
    if (this.inFormation) {
      return;
    }

    // Only check view periodically instead of every frame
    this.viewCheckCounter =
      (this.viewCheckCounter + 1) % Enemy.VIEW_CHECK_INTERVAL;
    if (this.viewCheckCounter === 0) {
      // Check if enemy is outside camera view
      this.isOutsideView = !this.scene.cameras.main.worldView.contains(
        this.x,
        this.y
      );

      if (this.isOutsideView) {
        // Disable collision checking and pause animation when outside view
        if (this.Body.checkCollision.none === false) {
          this.Body.checkCollision.none = true;
        }

        if (this.anims.isPlaying) {
          this.anims.pause();
        }

        this.timeOutsideScreen += delta * Enemy.VIEW_CHECK_INTERVAL; // Compensate for less frequent checks

        if (this.timeOutsideScreen >= Enemy.TIME_BEFORE_PROCESS_OUTSIDE_VIEW) {
          // If this enemy should teleport instead of despawn, do that
          if (this.isTeleportOnCull) {
            this.OnTeleportOnCull();
            this.timeOutsideScreen = 0; // Reset the timer after teleporting
          } else {
            this.DeSpawn();
          }
          return;
        }
      } else {
        // Re-enable collision checking and resume animation when back in view
        if (this.Body.checkCollision.none === true) {
          this.Body.checkCollision.none = false;
        }
        if (this.anims.isPaused) {
          this.anims.resume();
        }
        this.timeOutsideScreen = 0;
      }
    }

    // Skip movement calculations if outside view (but not too far)
    // if (this.isOutsideView && this.timeOutsideScreen > 1000) {
    //   return;
    // }

    // Cache player position for performance
    const playerX = this.target.x;
    const playerY = this.target.y;

    // Movement logic runs regardless of visibility
    if (
      !this.FixedDirection ||
      (this.FixedDirection &&
        this._currentDirection.x === 0 &&
        this._currentDirection.y === 0)
    ) {
      const dirX = playerX - this.x;
      const dirY = playerY - this.y;
      const length = Math.sqrt(dirX * dirX + dirY * dirY);

      if (length > 0) {
        this._currentDirection.x = dirX / length;
        this._currentDirection.y = dirY / length;
      }
    }

    const speed = GameCore.EnemySpeed * this.speed;
    const damage = this.receivingDamage ? -this.knockback * this.damageKB : 1;
    const velocityX = speed * this._currentDirection.x * damage;
    const velocityY = speed * this._currentDirection.y * damage;

    // Only update flip if direction changed significantly and enemy is visible
    if (!this.isOutsideView && Math.abs(this._currentDirection.x) > 0.1) {
      this.setFlipX(this._currentDirection.x > 0);
    }

    this.setVelocity(velocityX, velocityY);
  }
  Disappear() {
    this.DeSpawn();
  }

  DropCoin() {
    // For camels, drop coins based on caravan element data if available
    if (this.enemyType === EnemyType.CAMEL) {
      // Check if this is a caravan camel, with element data
      const caravanElement = this.getData("caravanElement") as CaravanElement;

      if (!caravanElement) {
        return;
      }

      // Use the regular value from the caravan element (both regular and special camels have this)
      const coinCount = caravanElement.value || 0;

      // Visual feedback - still spawn the coins but they're just visual
      // For display only, limited to avoid performance issues
      const displayCoinCount = Math.min(coinCount, 15);

      // Calculate if we need to create a special high-value coin for remaining value
      const remainingValue = coinCount - displayCoinCount;

      // Create the limited visual coins
      for (let i = 0; i < displayCoinCount; i++) {
        // Add some spread to the coins
        const offsetX = (Math.random() - 0.5) * 30;
        const offsetY = (Math.random() - 0.5) * 30;

        // Create a coin that doesn't attempt to claim a reward
        const coin = Game.Core.MakePickup(
          this.x + offsetX,
          this.y + offsetY,
          PickupType.COIN
        );

        // Mark as "from caravan" so it's added to caravanCoinsCollected
        if (coin && coin.itemType === PickupType.COIN) {
          (coin as CoinPickup).isFromCaravan = true;
        }
      }

      // If we have remaining value, create one special high-value coin
      if (remainingValue > 0) {
        const specialCoin = Game.Core.MakePickup(
          this.x,
          this.y,
          PickupType.COIN
        );

        if (specialCoin && specialCoin.itemType === PickupType.COIN) {
          const coinPickup = specialCoin as CoinPickup;
          coinPickup.isFromCaravan = true;
          coinPickup.SetValue(remainingValue);
        }
      }
    }
  }

  DeSpawn() {
    this.isDead = true;
    this.Body.enable = false;
    this.visible = false;

    // Clear the health bar when despawning
    if (this.bossHealthBar) {
      this.bossHealthBar.clear();
    }

    // Don't recycle formation enemies back to the pool
    if (this.inFormation) {
      // Just remove from active arrays without putting back in the pool
      const index = Game.Core.Enemies.indexOf(this);
      if (index > -1) {
        Game.Core.Enemies.splice(index, 1);
        Game.Core.EnemyGroup.remove(this, false);
      }

      // Reset formation flag
      this.inFormation = false;

      // Schedule destruction for next frame to avoid event listener conflicts
      this.scene.time.delayedCall(10, () => {
        // Remove from scene without calling destroy directly
        this.scene.children.remove(this);
      });
    } else {
      // Normal recycling for non-formation enemies
      this.pool.Recycle(this);
    }
  }

  public setInFormation(value: boolean) {
    this.inFormation = value;
  }

  private updateBossHealthBar() {
    if (!this.bossHealthBar || !this.isBoss) return;

    this.bossHealthBar.clear();
    const hpValue = this.hp / this.maxHp;

    // Position the health bar below the enemy
    const barX = this.x - this.bossHealthBarWidth / 2;
    const barY = this.y + this.bossHealthBarOffset.y;

    // Draw background
    this.bossHealthBar.fillStyle(0x000000, 1);
    this.bossHealthBar.fillRect(
      barX,
      barY,
      this.bossHealthBarWidth,
      this.bossHealthBarHeight
    );

    // Draw health value
    this.bossHealthBar.fillStyle(0xff0000, 1);
    this.bossHealthBar.fillRect(
      barX,
      barY,
      this.bossHealthBarWidth * hpValue,
      this.bossHealthBarHeight
    );
  }
}
